import axios from "axios";
import { ISignupFormState, passwordPolicyError } from "./signupFlow";

// Signup v2 service layer — the hot-swap boundary between the wizard UI and
// the platform (FLOCK-6611). The wizard only ever talks to ISignupService.
//
// `stub` emulates the prototype behavior (timeouts, any code verifies) so the
// UI can be exercised end to end with no backend. `live` wires the current
// platform mechanics one seam at a time. Toggle with SIGNUP_SERVICE_MODE.
//
// TODO(FLOCK-6611 follow-ups — see issue AC):
// 1. DONE (FLOCK-6670) — Embedded password: live.createAccount now creates the
//    Auth0 user directly with POST /dbconnections/signup, using the password
//    typed in StepAccount, instead of redirecting to Universal Login and asking
//    for it a second time. No login follows the call by design: a PostLogin
//    action denies sign-in until the email is verified, so the wizard advances
//    to verify-email and the user signs in at /login after clicking the link.
//    (PreUserRegistration still stamps app_metadata — it runs on this path too.)
// 2. Real OTP verification: current platform verification is link-based
//    (Auth0 hosted email + PostLogin deny + resend endpoint). To enable the
//    parked 6-digit UI in StepVerifyEmail: add an OTP issue/verify pair on
//    api-service (or Auth0 email-OTP passwordless), then implement
//    live.verifyEmailCode and flip VERIFY_MODE to 'code'.
// 3. HubSpot: today capture happens server-side inside /api/users/registration
//    (createCrmRecords). Follow-up moves to CRM v3 objects API with lead
//    capture on step-1 advance and a tell-admin workflow enrollment. Swap
//    live.submitLead / live.submitTellAdmin when that lands (FLOCK-6577
//    sandbox for QA).

export type SignupServiceMode = "stub" | "live";

// Default mode when neither the query override (?signupService=live|stub) nor
// the build-time VUE_APP_SIGNUP_SERVICE_MODE is present. Live: the QA build on
// 2026-09-03 shipped without the build-time var baked in, so every entry that
// lacked the query param (the login page's "Start your free trial" link, any
// hand-typed URL) ran the stub and created nothing. Live only means real calls
// to whatever VUE_APP_API_HOST / VUE_APP_AUTH0_* point at for that build; use
// ?signupService=stub for a no-backend demo.
export const SIGNUP_SERVICE_MODE: SignupServiceMode = "live";

// 'inbox' renders the check-your-inbox variant (v1, matches link-based
// verification). 'code' renders the parked 6-digit OTP entry.
export const VERIFY_MODE: "inbox" | "code" = "inbox";

// Which field a failure belongs to, so a step can render it inline. 'email' is
// collected on step 1, so the Account step surfaces it at step level with a way
// back rather than against an input it does not own.
export type SignupErrorField = "email" | "password";

export interface ISignupResult {
  success: boolean;
  reason?: string;
  field?: SignupErrorField;
}

export interface IEmailCheckResult {
  available: boolean;
  // 'in_use' when a real account exists; 'abandoned_signup' when an earlier
  // wizard run stopped before the Account step (registration replaces it);
  // 'free_email' when the domain is on the free/disposable provider list
  // (FLOCK-6777); 'unchecked' when the check itself failed (registration
  // still enforces).
  reason: "in_use" | "abandoned_signup" | "free_email" | "unchecked" | null;
}

export interface ISignupService {
  // Step 1, before advancing: is this email free to register? Live: GET
  // /api/users/emailavailable/:email. Duplicates are flagged where the email
  // is typed instead of one step later (Nisreen, 2026-09-04).
  checkEmail(email: string): Promise<IEmailCheckResult>;

  // Step 1 advance. v1: no-op (capture happens in registration). Follow-up:
  // early HubSpot lead capture so abandoned signups still become leads.
  submitLead(form: ISignupFormState): Promise<ISignupResult>;

  // Step 2 complete — the org-creation call. Live: POST /api/users/registration
  // (creates Mongo org + user, HubSpot contact + form submission, signature).
  submitRegistration(form: ISignupFormState): Promise<ISignupResult>;

  // Step 3 confirm — creates the Auth0 user from the password typed in the
  // wizard. Never redirects: on success the wizard advances to verify-email.
  createAccount(form: ISignupFormState): Promise<ISignupResult>;

  // Verify screen. 'inbox' mode: resend only. 'code' mode: parked until OTP.
  resendVerification(form: ISignupFormState): Promise<ISignupResult>;
  verifyEmailCode(form: ISignupFormState, code: string): Promise<ISignupResult>;

  // Side flow
  submitTellAdmin(payload: ITellAdminPayload): Promise<ISignupResult>;
  checkRoomCode(code: string): Promise<ISignupResult>;
}

export interface ITellAdminPayload {
  firstName: string;
  lastName: string;
  email: string;
  orgName: string;
  adminEmail: string;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// --- Stub implementation (prototype parity) ---

const stub: ISignupService = {
  async checkEmail() {
    await delay(300);
    return { available: true, reason: null };
  },

  async submitLead() {
    return { success: true };
  },

  async submitRegistration(form: ISignupFormState) {
    await delay(700);
    form.registeredUserId = "stub-user-id";
    form.registeredOrgId = "stub-org-id";
    form.auth0Signature = "stub-signature";
    return { success: true };
  },

  async createAccount() {
    await delay(700);
    return { success: true };
  },

  async resendVerification() {
    await delay(500);
    return { success: true };
  },

  // Prototype behavior: any 6 digits pass.
  async verifyEmailCode() {
    await delay(1000);
    return { success: true };
  },

  async submitTellAdmin() {
    await delay(600);
    return { success: true };
  },

  async checkRoomCode() {
    await delay(700);
    return { success: true };
  }
};

// --- Live implementation (wired seam by seam) ---

// The Auth0 database connection the embedded signup call targets. Read from the
// environment the same way main.ts reads the domain and client id, so a tenant
// with a differently named connection needs no code change. The fallback is the
// stock Auth0 connection name, which is what our tenant uses today.
const AUTH0_CONNECTION =
  process.env.VUE_APP_AUTH0_CONNECTION || "Username-Password-Authentication";

const live: ISignupService = {
  async checkEmail(email: string) {
    try {
      const url = `${process.env.VUE_APP_API_HOST}/api/users/emailavailable/${encodeURIComponent(email)}`;
      const response = await axios.get(url);
      return {
        available: response.data.available !== false,
        reason: response.data.reason || null
      };
    } catch (e) {
      // Fail open: registration still rejects a duplicate on the next step.
      return { available: true, reason: "unchecked" };
    }
  },

  async submitLead() {
    // v1: capture happens server-side in submitRegistration. See TODO 3.
    return { success: true };
  },

  async submitRegistration(form: ISignupFormState) {
    try {
      const hutk = findCookieValue("hubspotutk");
      const registerUrl = `${process.env.VUE_APP_API_HOST}/api/users/registration`;
      const response = await axios.post(registerUrl, {
        name: `${form.firstName} ${form.lastName}`,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        orgName: form.orgName,
        // Keys must match IUserRegistrationModel on the API — anything else is
        // stripped by the strict model and silently never reaches HubSpot.
        market: form.market,
        jobFunction: form.jobFunction,
        phone: form.phone,
        country: form.country,
        state: form.country === "United States" ? form.state : "",
        numDisplays: form.numDisplays,
        // Free text; not sent as usageTypes, which feeds a two-option
        // checkbox property (ditto_initial_use_case) on the HubSpot side.
        plannedUse: form.plannedUse,
        pid: form.pid,
        hubspotutk: hutk
      });

      const user = response.data;
      form.registeredUserId = user.id;
      form.registeredOrgId = user.organization ? user.organization.id : null;
      form.auth0Signature = user.auth0Signature;
      // Diagnostic (FLOCK-6611): the API reports what happened to the HubSpot
      // form submission. Visible in the browser console on any environment.
      // eslint-disable-next-line no-console
      console.info("[signup] HubSpot form submission", user.crm || "(no crm diagnostic in response)");
      return { success: true };
    } catch (e) {
      const response = axios.isAxiosError(e) ? e.response : undefined;
      const emailError =
        response &&
        response.data &&
        response.data.errors &&
        response.data.errors.email &&
        response.data.errors.email.message;
      return {
        success: false,
        reason:
          emailError || "There was an error during signup. Please try again."
      };
    }
  },

  // Creates the Auth0 user from the password the wizard already collected.
  // /dbconnections/signup is Auth0's public signup endpoint: it takes the
  // client id in the body, needs no secret and no auth header, and it only
  // creates the user — it issues no session and no tokens.
  //
  // That is deliberate. A PostLogin action denies sign-in until the email is
  // verified, so there is nothing to log into yet; the wizard continues to its
  // verify-email step and the user signs in at /login after clicking the link.
  // This is why no cross-origin/embedded login is involved here.
  async createAccount(form: ISignupFormState) {
    const domain = process.env.VUE_APP_AUTH0_DOMAIN;
    const clientId = process.env.VUE_APP_AUTH0_CLIENTID;
    if (!domain || !clientId) {
      console.error(
        "Signup: VUE_APP_AUTH0_DOMAIN / VUE_APP_AUTH0_CLIENTID are not configured."
      );
      return {
        success: false,
        reason: "Signup is unavailable right now. Please try again later."
      };
    }

    // Guard rather than trust the caller: the service is the hot-swap boundary,
    // so it must not assume a particular step validated the password first.
    const policyError = passwordPolicyError(form.password);
    if (policyError) {
      return { success: false, field: "password", reason: policyError };
    }

    try {
      await axios.post(`https://${domain}/dbconnections/signup`, {
        client_id: clientId,
        email: form.email,
        password: form.password,
        connection: AUTH0_CONNECTION
      });
      return { success: true };
    } catch (e) {
      return mapAuth0SignupError(e);
    }
  },

  async resendVerification(form: ISignupFormState) {
    if (!form.registeredUserId)
      return { success: false, reason: "No user to verify." };
    try {
      const url = `${process.env.VUE_APP_API_HOST}/api/users/${form.registeredUserId}/send/verification`;
      await axios.get(url);
      return { success: true };
    } catch (e) {
      return {
        success: false,
        reason: "Could not resend the verification email."
      };
    }
  },

  async verifyEmailCode() {
    // Parked until an OTP mechanism exists. See TODO 2.
    return {
      success: false,
      reason: "Code verification is not available yet."
    };
  },

  async submitTellAdmin() {
    // Stub even in live mode until the HubSpot workflow exists. See TODO 3.
    await delay(600);
    return { success: true };
  },

  async checkRoomCode() {
    // Internal connect flow is parked; CTA links to connect.goditto.com.
    await delay(700);
    return { success: true };
  }
};

// Error body shape of /dbconnections/signup. Auth0 names the failure `code` on
// most responses and `error` on others, and `description` is a sentence for
// most codes but an object (rule tree) for password-strength failures — which
// is why `policy` carries the readable rule list.
interface IAuth0SignupError {
  code?: string;
  error?: string;
  description?: string | Record<string, unknown>;
  policy?: string;
}

// Maps an Auth0 signup failure onto the field the wizard can actually show it
// against. Unrecognized codes fall through to a generic message so raw Auth0
// copy never reaches the user.
function mapAuth0SignupError(e: unknown): ISignupResult {
  const response = axios.isAxiosError(e) ? e.response : undefined;
  const body: IAuth0SignupError =
    response && response.data ? response.data : {};
  const code = body.code || body.error;

  // invalid_signup is what the tenant returns when "disable signup error
  // message details" is on and the real user_exists code is withheld, so both
  // land on the same message.
  if (
    code === "user_exists" ||
    code === "username_exists" ||
    code === "invalid_signup"
  ) {
    return {
      success: false,
      field: "email",
      reason: "An account with this email already exists."
    };
  }

  if (code === "invalid_password") {
    // Dictionary/user-info variants put a readable sentence in `description`;
    // strength failures put the rule list in `policy` and leave `description`
    // as an object. Prefer whichever is human-readable.
    const description =
      typeof body.description === "string" ? body.description : "";
    const policy = body.policy ? body.policy.replace(/\s*\n\s*/g, " ") : "";
    return {
      success: false,
      field: "password",
      reason:
        description ||
        policy ||
        "That password does not meet the password policy. Please choose a stronger one."
    };
  }

  if (code === "requires_verification") {
    // Auth0 bot detection is challenging the request with a captcha. Embedded
    // signup has no way to answer one, so this is a tenant configuration
    // problem (Bot Detection setting), not something the user can fix — log it
    // distinctly so it is recognizable in the console and in error reporting.
    console.error(
      "Signup: Auth0 bot detection returned requires_verification. Embedded /dbconnections/signup cannot answer a captcha — the tenant's Bot Detection setting needs to change."
    );
    return {
      success: false,
      reason:
        "We could not verify this request. Please try again later or contact support."
    };
  }

  console.error("Signup: unexpected Auth0 signup error", code || e);
  return {
    success: false,
    reason: "There was an error creating your account. Please try again."
  };
}

function findCookieValue(cookieKey: string): string | undefined {
  const cookieArray = document.cookie.split(";");
  const cookieValue = cookieArray.find(arrayVal => {
    const keyVal = arrayVal.split("=");
    return keyVal[0].toLowerCase().trim() === cookieKey.toLowerCase();
  });
  if (!cookieValue) return undefined;
  return cookieValue.split("=")[1];
}

export function getSignupService(): ISignupService {
  const params = new URLSearchParams(window.location.search);
  const override = params.get("signupService");
  if (override === "live") return live;
  if (override === "stub") return stub;
  // Deploy-time control: set VUE_APP_SIGNUP_SERVICE_MODE=live in the QA env
  // to default to live wiring without a query param.
  const envMode = process.env.VUE_APP_SIGNUP_SERVICE_MODE;
  if (envMode === "live" || envMode === "stub") {
    return envMode === "live" ? live : stub;
  }
  return SIGNUP_SERVICE_MODE === "live" ? live : stub;
}
